import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit, Trash2, Shield, CheckCircle, XCircle } from 'lucide-react';
import { roleService } from '../../services/roleService';
import { HmgRole } from '../../types';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import RoleFormModal from './RoleFormModal';
import RolePermissionsModal from './RolePermissionsModal';

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<HmgRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<HmgRole | null>(null);
  const [managingPermissionsRole, setManagingPermissionsRole] = useState<HmgRole | null>(null);
  const [deletingRole, setDeletingRole] = useState<HmgRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await roleService.getAll(search);
      setRoles(data);
    } catch (err: any) {
      setError('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleAdd = () => {
    setEditingRole(null);
    setIsFormOpen(true);
  };

  const handleEdit = (role: HmgRole) => {
    setEditingRole(role);
    setIsFormOpen(true);
  };

  const handleManagePermissions = (role: HmgRole) => {
    setManagingPermissionsRole(role);
    setIsPermissionsOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRole) return;

    try {
      await roleService.delete(deletingRole.id);
      setSuccess('Perfil excluído com sucesso!');
      setDeletingRole(null);
      loadRoles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir perfil');
      setDeletingRole(null);
    }
  };

  const handleToggleActive = async (role: HmgRole) => {
    try {
      await roleService.toggleActive(role.id);
      setSuccess(`Perfil ${role.is_active ? 'desativado' : 'ativado'} com sucesso!`);
      loadRoles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar perfil');
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingRole(null);
    loadRoles();
    setSuccess(editingRole ? 'Perfil atualizado com sucesso!' : 'Perfil criado com sucesso!');
  };

  const handlePermissionsSuccess = () => {
    setIsPermissionsOpen(false);
    setManagingPermissionsRole(null);
    setSuccess('Permissões atualizadas com sucesso!');
    loadRoles();
  };

  if (loading && roles.length === 0) {
    return (
      <div className="p-8 flex justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Alerts */}
      {error && (
        <Alert type="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Perfis de Acesso</h2>
          <p className="text-gray-600 mt-1">
            Gerencie os perfis e suas permissões de acesso
          </p>
        </div>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Novo Perfil
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder="Buscar perfil..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="h-5 w-5" />}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Perfil
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuários
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-500" />
                      {role.name}
                    </div>
                    {role.description && (
                      <div className="text-sm text-gray-500 mt-1">{role.description}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant="info">{role.users_count || 0} usuários</Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={role.is_active ? 'success' : 'danger'}>
                    {role.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleManagePermissions(role)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                      title="Gerenciar Permissões"
                    >
                      <Shield className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(role)}
                      className={`p-2 rounded-md transition-colors ${
                        role.is_active
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={role.is_active ? 'Desativar' : 'Ativar'}
                    >
                      {role.is_active ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(role)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeletingRole(role)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Excluir"
                      disabled={(role.users_count || 0) > 0}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {roles.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Nenhum perfil encontrado
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <RoleFormModal
          role={editingRole}
          onClose={() => {
            setIsFormOpen(false);
            setEditingRole(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Permissions Modal */}
      {isPermissionsOpen && managingPermissionsRole && (
        <RolePermissionsModal
          role={managingPermissionsRole}
          onClose={() => {
            setIsPermissionsOpen(false);
            setManagingPermissionsRole(null);
          }}
          onSuccess={handlePermissionsSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingRole && (
        <Modal
          isOpen={true}
          onClose={() => setDeletingRole(null)}
          title="Confirmar Exclusão"
        >
          <div className="p-6">
            <p className="text-gray-700 mb-6">
              Tem certeza que deseja excluir o perfil{' '}
              <strong>{deletingRole.name}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeletingRole(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Excluir
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RolesPage;
